import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, LogIn, UserPlus, Eye, EyeOff, X } from "lucide-react";
import { Logo } from "../components/common/Logo";
import {
  BrutalButton,
  BrutalCard,
  BrutalInput,
  BrutalLabel,
  Badge,
} from "../components/common/Brutal";
import { api, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const COUNTRIES = ["Philippines", "United States", "Canada", "United Kingdom", "Australia", "Japan", "Singapore", "Other"];
const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleSignIn = () => {
  const state = window.crypto?.randomUUID?.() || `kf-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  window.localStorage.setItem("kf_google_oauth_state", state);
  window.location.href = `${API}/auth/google/start?state=${encodeURIComponent(state)}`;
};

export default function Login() {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith("/admin");
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const [mode, setMode] = useState("login");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [otp, setOtp] = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showLoginPw, setShowLoginPw] = useState(false);

  // Signup form
  const empty = {
    lastname: "", firstname: "", middlename: "", suffix: "",
    birthdate: "", gender: "", mobile_number: "", email: "",
    password: "", country: "Philippines", address: "",
    province: "", municipality: "", barangay: "",
  };
  const [su, setSu] = useState(empty);
  const [suRetype, setSuRetype] = useState("");
  const [showSuPw, setShowSuPw] = useState(false);
  const [showSuRetype, setShowSuRetype] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);

  // Forgot password flow
  const [forgot, setForgot] = useState({ open: false, stage: "email", userId: "", otp: "", mockOtp: "", newPw: "", retypePw: "" });
  const [showForgotPw, setShowForgotPw] = useState(false);
  const [showForgotRetype, setShowForgotRetype] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/camper");
  }, [user, navigate]);

  useEffect(() => {
    if (mode === "signup" && su.country === "Philippines" && provinces.length === 0) {
      api.get("/ph/provinces").then(({ data }) => setProvinces(data));
    }
  }, [mode, su.country, provinces.length]);

  const onProvinceChange = async (code) => {
    setSu((s) => ({ ...s, province: code, municipality: "", barangay: "" }));
    setMunicipalities([]);
    setBarangays([]);
    if (code) {
      const { data } = await api.get(`/ph/municipalities/${code}`);
      setMunicipalities(data);
    }
  };
  const onMunChange = async (code) => {
    setSu((s) => ({ ...s, municipality: code, barangay: "" }));
    setBarangays([]);
    if (code) {
      const { data } = await api.get(`/ph/barangays/${code}`);
      setBarangays(data);
    }
  };

  const submitLogin = async (e) => {
    e?.preventDefault();
    if (!loginForm.email || !loginForm.password) return toast.error("Email and password required");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", loginForm);
      setUserId(data.user_id);
      setStep(2);
      toast.success("Verification code sent to your email.");
      if (!data.email_verified) {
        toast.message("Email not verified yet — please verify with OTP to continue.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const submitSignup = async (e) => {
    e?.preventDefault();
    const required = ["lastname","firstname","birthdate","gender","mobile_number","email","password","country","address"];
    for (const k of required) if (!su[k]) return toast.error(`Missing: ${k.replace("_"," ")}`);
    if (su.country === "Philippines" && (!su.province || !su.municipality || !su.barangay))
      return toast.error("Province, municipality and barangay are required for Philippines");
    if (su.password.length < 6) return toast.error("Password must be at least 6 chars");
    if (su.password !== suRetype) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", su);
      setUserId(data.user_id);
      setStep(2);
      toast.success("Account created. Verification code sent to your email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp) return toast.error("Enter OTP");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { user_id: userId, otp });
      login(data.token, data.user);
      toast.success("Welcome to KAOGMA!");
      navigate(data.user.role === "admin" ? "/admin" : "/camper");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    try {
      await api.post("/auth/resend-otp", { user_id: userId });
      toast.success("New verification code sent.");
    } catch { toast.error("Resend failed"); }
  };

  // Forgot password handlers
  const requestReset = async (email) => {
    if (!email) return toast.error("Enter your email");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setForgot((f) => ({ ...f, stage: "otp", userId: data.user_id, mockOtp: data.otp }));
      toast.success(`OTP sent (mock): ${data.otp}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No camper account found");
    }
  };

  const submitNewPassword = async () => {
    if (forgot.newPw.length < 6) return toast.error("Password must be at least 6 chars");
    if (forgot.newPw !== forgot.retypePw) return toast.error("Passwords do not match");
    try {
      const { data } = await api.post("/auth/reset-password", {
        user_id: forgot.userId,
        otp: forgot.otp,
        new_password: forgot.newPw,
      });
      login(data.token, data.user);
      toast.success("Password updated. Welcome back!");
      setForgot({ open: false, stage: "email", userId: "", otp: "", mockOtp: "", newPw: "", retypePw: "" });
      navigate("/camper");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    }
  };

  const verifyForgotOtp = () => {
    if (!forgot.otp || forgot.otp.length < 6) return toast.error("Enter the 6-digit OTP");
    setForgot((f) => ({ ...f, stage: "newpw" }));
  };

  const heroTitle = isAdminPath
    ? "Admin Console"
    : step === 2
      ? "Verify OTP"
      : "KAOGMA FESTIVAL 2026";
  const heroSub = !isAdminPath && step !== 2 ? "The Hottest Festival in the World!" : null;

  return (
    <div className="min-h-screen bg-[#FFF4E6] kf-grain flex flex-col">
      <header className="border-b-2 border-[#3D1E6D] bg-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/" className="text-xs font-bold uppercase tracking-wider text-[#3D1E6D] hover:text-[#E5247C] inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3 w-3" /> Home
          </Link>
        </div>
      </header>

      <div className="flex-1 grid place-items-center px-4 py-8">
        <BrutalCard className="w-full max-w-2xl p-6 sm:p-8" data-testid="login-card">
          <div className="flex flex-col items-center text-center mb-6">
            <Logo size="xl" showText={false} />
            <div className="display text-3xl text-[#3D1E6D] mt-4 leading-tight" data-testid="login-title">
              {heroTitle}
            </div>
            {heroSub && (
              <div className="text-sm font-semibold text-[#6B4E9E] mt-1" data-testid="login-subtitle">
                {heroSub}
              </div>
            )}
          </div>

          {step === 1 && !isAdminPath && (
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="auth-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`px-3 py-2.5 border-2 border-[#3D1E6D] font-bold text-sm uppercase tracking-wider transition-all ${
                  mode === "login" ? "bg-[#E5247C] text-white shadow-[3px_3px_0_#3D1E6D]" : "bg-white"
                }`}
                data-testid="mode-login"
              >
                <LogIn className="h-4 w-4 inline mr-1" /> Login
              </button>
              <button
                type="button"
                onClick={() => setMode("signup")}
                className={`px-3 py-2.5 border-2 border-[#3D1E6D] font-bold text-sm uppercase tracking-wider transition-all ${
                  mode === "signup" ? "bg-[#FFB400] shadow-[3px_3px_0_#3D1E6D]" : "bg-white"
                }`}
                data-testid="mode-signup"
              >
                <UserPlus className="h-4 w-4 inline mr-1" /> Sign Up
              </button>
            </div>
          )}

          {step === 1 && mode === "login" && (
            <form onSubmit={submitLogin} className="space-y-4">
              <div>
                <BrutalLabel>Email (Username)</BrutalLabel>
                <BrutalInput
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder={isAdminPath ? "admin@kaogma.com" : "you@example.com"}
                  data-testid="login-email"
                />
              </div>
              <div>
                <BrutalLabel>Password</BrutalLabel>
                <PasswordInput
                  value={loginForm.password}
                  onChange={(v) => setLoginForm({ ...loginForm, password: v })}
                  show={showLoginPw}
                  onToggle={() => setShowLoginPw((v) => !v)}
                  testid="login-password"
                />
                {!isAdminPath && (
                  <button
                    type="button"
                    onClick={() => setForgot({ open: true, stage: "email", userId: "", otp: "", mockOtp: "", newPw: "", retypePw: "" })}
                    className="mt-2 text-xs font-bold uppercase tracking-wider text-[#E5247C] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                )}
              </div>
              <BrutalButton type="submit" variant="primary" className="w-full" disabled={loading} data-testid="login-submit">
                {loading ? "Sending..." : "Login"} <ArrowRight className="h-4 w-4" />
              </BrutalButton>
              {isAdminPath && (
                <p className="text-xs text-[#6B4E9E] text-center">
                  Demo admin: <b>admin@kaogma.com</b> · password <b>admin123</b>
                </p>
              )}
              {!isAdminPath && (
                <>
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-0.5 bg-[#3D1E6D]/20" />
                    <span className="text-xs uppercase tracking-widest text-[#6B4E9E]">or</span>
                    <div className="flex-1 h-0.5 bg-[#3D1E6D]/20" />
                  </div>
                  <BrutalButton type="button" variant="outline" className="w-full" onClick={googleSignIn} data-testid="google-signin">
                    <GoogleIcon /> Continue with Google
                  </BrutalButton>
                </>
              )}
            </form>
          )}

          {step === 1 && mode === "signup" && (
            <form onSubmit={submitSignup} className="space-y-4">
              {/* Mobile-first order matches user spec; sm:grid-cols-2 keeps desktop pairing */}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Last Name *"><BrutalInput value={su.lastname} onChange={(e) => setSu({...su, lastname: e.target.value})} data-testid="su-lastname" /></Field>
                <Field label="First Name *"><BrutalInput value={su.firstname} onChange={(e) => setSu({...su, firstname: e.target.value})} data-testid="su-firstname" /></Field>
                <Field label="Middle Name"><BrutalInput value={su.middlename} onChange={(e) => setSu({...su, middlename: e.target.value})} data-testid="su-middlename" /></Field>
                <Field label="Suffix (Jr, Sr…)"><BrutalInput value={su.suffix} onChange={(e) => setSu({...su, suffix: e.target.value})} data-testid="su-suffix" /></Field>
                <Field label="Birthdate *"><BrutalInput type="date" value={su.birthdate} onChange={(e) => setSu({...su, birthdate: e.target.value})} data-testid="su-birthdate" className="w-full" /></Field>
                <Field label="Gender *">
                  <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={su.gender} onChange={(e) => setSu({...su, gender: e.target.value})} data-testid="su-gender">
                    <option value="">Select…</option>
                    {GENDERS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <Field label="Mobile Number *">
                  <div className="flex">
                    <span className="bg-[#FFB400] border-2 border-r-0 border-[#3D1E6D] px-3 grid place-items-center font-bold text-sm">+63</span>
                    <BrutalInput value={su.mobile_number} onChange={(e) => setSu({...su, mobile_number: e.target.value.replace(/\D/g, "").slice(0, 10)})} placeholder="9171234567" data-testid="su-mobile" />
                  </div>
                </Field>
                <Field label="Email Address *"><BrutalInput type="email" value={su.email} onChange={(e) => setSu({...su, email: e.target.value})} data-testid="su-email" /></Field>
                <Field label="Country *">
                  <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={su.country} onChange={(e) => setSu({...su, country: e.target.value, province: "", municipality: "", barangay: ""})} data-testid="su-country">
                    {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <BrutalLabel>Address (House #, Street) *</BrutalLabel>
                  <BrutalInput value={su.address} onChange={(e) => setSu({...su, address: e.target.value})} data-testid="su-address" />
                </div>
                {su.country === "Philippines" && (
                  <>
                    <Field label="Province *">
                      <Select value={su.province} onValueChange={onProvinceChange}>
                        <SelectTrigger className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 h-auto" data-testid="su-province">
                          <SelectValue placeholder="Select province…" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(60vh,20rem)] z-[60]">
                          {provinces.map((p) => (
                            <SelectItem key={p.code} value={p.code}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Municipality / City *">
                      <Select value={su.municipality} onValueChange={onMunChange} disabled={!municipalities.length}>
                        <SelectTrigger className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 h-auto" data-testid="su-municipality">
                          <SelectValue placeholder={municipalities.length ? "Select…" : "Pick province first"} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(60vh,20rem)] z-[60]">
                          {municipalities.map((m) => (
                            <SelectItem key={m.code} value={m.code}>
                              {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <div className="sm:col-span-2">
                      <BrutalLabel>Barangay *</BrutalLabel>
                      <Select value={su.barangay} onValueChange={(value) => setSu((s) => ({ ...s, barangay: value }))} disabled={!barangays.length}>
                        <SelectTrigger className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5 h-auto" data-testid="su-barangay">
                          <SelectValue placeholder={barangays.length ? "Select…" : "Pick municipality first"} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(60vh,20rem)] z-[60]">
                          {barangays.map((b) => (
                            <SelectItem key={b.code} value={b.code}>
                              {b.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <Field label="Password (min 6 chars) *">
                  <PasswordInput
                    value={su.password}
                    onChange={(v) => setSu({...su, password: v})}
                    show={showSuPw}
                    onToggle={() => setShowSuPw((v) => !v)}
                    testid="su-password"
                  />
                </Field>
                <Field label="Retype Password *">
                  <PasswordInput
                    value={suRetype}
                    onChange={setSuRetype}
                    show={showSuRetype}
                    onToggle={() => setShowSuRetype((v) => !v)}
                    testid="su-retype"
                  />
                </Field>
              </div>
              <BrutalButton type="submit" variant="primary" className="w-full" disabled={loading} data-testid="signup-submit">
                {loading ? "Creating..." : "Create Account & Send OTP"} <ArrowRight className="h-4 w-4" />
              </BrutalButton>
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 h-0.5 bg-[#3D1E6D]/20" />
                <span className="text-xs uppercase tracking-widest text-[#6B4E9E]">or</span>
                <div className="flex-1 h-0.5 bg-[#3D1E6D]/20" />
              </div>
              <BrutalButton type="button" variant="outline" className="w-full" onClick={googleSignIn} data-testid="google-signin">
                <GoogleIcon /> Sign up with Google
              </BrutalButton>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <div className="kf-border bg-[#FFB400] p-3 flex items-start gap-2">
                <Badge color="dark">EMAIL SENT</Badge>
                <div className="text-sm text-[#3D1E6D]">
                  Check your email for the verification code.
                  <div className="text-[10px] uppercase tracking-widest mt-1">Expires in 5 minutes</div>
                </div>
              </div>
              <div>
                <BrutalLabel>6-digit OTP</BrutalLabel>
                <BrutalInput
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="tracking-[0.5em] text-center text-2xl font-bold"
                  data-testid="otp-input"
                  autoFocus
                />
              </div>
              <BrutalButton type="submit" variant="primary" className="w-full" disabled={loading} data-testid="otp-verify-submit">
                {loading ? "Verifying..." : "Verify & Continue"}
              </BrutalButton>
              <button type="button" onClick={resendOtp} className="w-full text-xs font-bold uppercase tracking-wider text-[#E5247C] hover:underline" data-testid="otp-resend">
                Resend OTP
              </button>
            </form>
          )}
        </BrutalCard>
      </div>

      {forgot.open && (
        <ForgotPasswordModal
          forgot={forgot}
          setForgot={setForgot}
          onRequest={requestReset}
          onVerify={verifyForgotOtp}
          onSubmit={submitNewPassword}
          showPw={showForgotPw}
          setShowPw={setShowForgotPw}
          showRetype={showForgotRetype}
          setShowRetype={setShowForgotRetype}
        />
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);

const PasswordInput = ({ value, onChange, show, onToggle, testid }) => (
  <div className="relative">
    <BrutalInput
      type={show ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pr-10"
      data-testid={testid}
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#3D1E6D] hover:text-[#E5247C]"
      aria-label={show ? "Hide password" : "Show password"}
      data-testid={`${testid}-toggle`}
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  </div>
);

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
);

const ForgotPasswordModal = ({ forgot, setForgot, onRequest, onVerify, onSubmit, showPw, setShowPw, showRetype, setShowRetype }) => {
  const [email, setEmail] = useState("");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={() => setForgot({ open: false, stage: "email", userId: "", otp: "", mockOtp: "", newPw: "", retypePw: "" })}
      data-testid="forgot-password-modal"
    >
      <BrutalCard className="w-full max-w-md p-6 bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="display text-2xl text-[#3D1E6D]">Reset Password</div>
          <button
            onClick={() => setForgot({ open: false, stage: "email", userId: "", otp: "", mockOtp: "", newPw: "", retypePw: "" })}
            className="kf-border bg-white p-1.5 hover:bg-[#FFB400]"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {forgot.stage === "email" && (
          <div className="space-y-3">
            <p className="text-sm text-[#6B4E9E]">Enter your registered camper email. We&apos;ll send a 6-digit OTP valid for 5 minutes.</p>
            <BrutalLabel>Email</BrutalLabel>
            <BrutalInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="forgot-email" autoFocus />
            <BrutalButton variant="primary" className="w-full" onClick={() => onRequest(email)} data-testid="forgot-request">
              Send OTP <ArrowRight className="h-4 w-4" />
            </BrutalButton>
          </div>
        )}

        {forgot.stage === "otp" && (
          <div className="space-y-3">
            <div className="kf-border bg-[#FFB400] p-3 flex items-start gap-2">
              <Badge color="dark">MOCK EMAIL</Badge>
              <div className="text-sm text-[#3D1E6D]">
                Your OTP is <b className="display tracking-widest text-xl">{forgot.mockOtp}</b>
                <div className="text-[10px] uppercase tracking-widest mt-1">Expires in 5 minutes</div>
              </div>
            </div>
            <BrutalLabel>6-digit OTP</BrutalLabel>
            <BrutalInput
              value={forgot.otp}
              onChange={(e) => setForgot((f) => ({ ...f, otp: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
              placeholder="000000"
              className="tracking-[0.5em] text-center text-2xl font-bold"
              data-testid="forgot-otp"
              autoFocus
            />
            <BrutalButton variant="primary" className="w-full" onClick={onVerify} data-testid="forgot-verify">
              Verify & Continue
            </BrutalButton>
          </div>
        )}

        {forgot.stage === "newpw" && (
          <div className="space-y-3">
            <p className="text-sm text-[#6B4E9E]">Choose a new password (min 6 chars).</p>
            <div>
              <BrutalLabel>New Password</BrutalLabel>
              <PasswordInput
                value={forgot.newPw}
                onChange={(v) => setForgot((f) => ({ ...f, newPw: v }))}
                show={showPw}
                onToggle={() => setShowPw((v) => !v)}
                testid="forgot-newpw"
              />
            </div>
            <div>
              <BrutalLabel>Retype New Password</BrutalLabel>
              <PasswordInput
                value={forgot.retypePw}
                onChange={(v) => setForgot((f) => ({ ...f, retypePw: v }))}
                show={showRetype}
                onToggle={() => setShowRetype((v) => !v)}
                testid="forgot-retype"
              />
            </div>
            <BrutalButton variant="primary" className="w-full" onClick={onSubmit} data-testid="forgot-submit">
              Update Password & Sign In <ArrowRight className="h-4 w-4" />
            </BrutalButton>
          </div>
        )}
      </BrutalCard>
    </div>
  );
};
